import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import UserHeader from '../../components/header/UserHeader';

const HelpPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('buyers');

  const faqData = {
    buyers: [
      {
        question: "How do I create an account?",
        answer: "Click on 'User Login' from the homepage, then select 'Register' to create a new account. You'll need to provide your email, name, and create a password."
      },
      {
        question: "How do I place an order?",
        answer: "Browse products, add them to your cart, and proceed to checkout. You'll need to provide shipping and payment information to complete your order."
      },
      {
        question: "How can I track my order?",
        answer: "Once your order is placed, you can track it in real-time from your dashboard. You'll also receive email notifications about order status updates."
      },
      {
        question: "What payment methods are accepted?",
        // The three options PaymentPage actually renders. This answer used to
        // list PayPal, Apple Pay and Google Pay, none of which are offered.
        answer: "Pay by UPI, or by card, wallet or net banking, or choose Cash on Delivery and pay when your order arrives."
      },
      {
        question: "How do I return a product?",
        // 24 hours from delivery is what Order.checkReturnEligibility actually
        // enforces, and what the orders page and return modal already tell the
        // buyer. This answer used to say 30 days from purchase.
        answer: "Go to 'My Orders' and use the Return button on the order within 24 hours of delivery. The page shows exactly how long you have left, and once you pick a reason your request is approved straight away."
      }
    ],
    sellers: [
      {
        question: "How do I become a seller?",
        answer: "Click on 'Seller Login' from the homepage and select 'Register'. You'll need to provide business information and complete the verification process."
      },
      {
        question: "How do I add products to my store?",
        answer: "Log into your seller dashboard and click 'Upload Catalogue'. Fill in all required information including images, description, and pricing. Your uploads then appear under 'My Catalogues', where you can edit a product, add a size variant, or pause it."
      },
      {
        question: "How do I receive payments?",
        answer: "Payments are processed securely and transferred to your registered bank account within 3-5 business days after order completion."
      },
      {
        question: "Can I set my own shipping rates?",
        answer: "Yes, you can set custom shipping rates based on weight, location, and delivery speed. You can also offer free shipping for orders above a certain amount."
      },
      {
        question: "How do I handle customer inquiries?",
        answer: "You'll receive real-time notifications for customer messages. Respond promptly to maintain good ratings and customer satisfaction."
      }
    ],
    general: [
      {
        question: "Is my personal information secure?",
        answer: "Yes, we use industry-standard encryption and security measures to protect your personal and payment information."
      },
      {
        question: "What if I forget my password?",
        answer: "Click on 'Forgot Password' on the login page. You'll receive an email with instructions to reset your password."
      },
      {
        question: "How do I contact customer support?",
        answer: "You can reach us through our contact form, email at zammer.retail2026@gmail.com or Info.zammer@gmail.com, or call us at +91 95127 12841 during business hours."
      },
      {
        question: "Do you ship internationally?",
        answer: "Currently, we ship to most countries. Shipping rates and delivery times vary by location. Check the shipping calculator during checkout."
      },
      {
        question: "What is your privacy policy?",
        answer: "We respect your privacy and never share your personal information with third parties without your consent. Read our full privacy policy for details."
      }
    ]
  };

  const tabs = [
    { key: 'buyers', label: 'Buyers' },
    { key: 'sellers', label: 'Sellers' },
    { key: 'general', label: 'General' }
  ];

  return (
    <>
      <UserHeader />
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Page sub-header */}
        <div className="flex items-center gap-1.5 px-3 h-12 bg-white border-b border-gray-100">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-gray-700"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <span className="text-[15px] font-semibold text-gray-900">Help &amp; FAQ</span>
        </div>

        <div className="px-4 py-5 space-y-4">
          {/* Intro */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Help &amp; FAQ</h1>
            <p className="text-sm text-gray-500 mt-1">Find answers to common questions.</p>
          </div>

          {/* Tab navigation */}
          <div className="flex gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* FAQ content */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="space-y-5">
              {faqData[activeTab].map((faq, index) => (
                <div
                  key={index}
                  className="border-b border-gray-100 pb-5 last:border-b-0 last:pb-0"
                >
                  <h3 className="text-sm font-semibold text-gray-900 mb-1.5">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact support */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-center">
            <h2 className="text-base font-semibold text-gray-900 mb-1.5">
              Still need help?
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Link
              to="/contact"
              className="block w-full bg-gray-900 text-white py-3 rounded-lg text-sm font-semibold hover:bg-black transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpPage;
